import React from "react";
import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";

import get from "lodash/get";
import clsx from "clsx";
import dayjs from "dayjs";

import { useTranslation } from "react-i18next";

import Accordion from "react-bootstrap/Accordion";
import Card from "react-bootstrap/Card";

import { QUERY_ACCESS_EVENT } from "../../../../config/graphql/query";
import {
  PostCheckinFormMarker,
  postCheckinFormState,
} from "../../../../lib/common/postCheckinFormState";
import checkOutMethod, {
  CheckOutMarker,
} from "../../../../lib/common/checkOutMethod";

export const getOwnerField = (
  owner: AccessEventOwner | undefined,
  fieldName: string,
): string => {
  if (!owner) {
    return "";
  }

  if (fieldName in owner) {
    return get(owner, [fieldName]);
  }

  if ("fields" in owner) {
    const field = owner.fields.find((field) => field.name === fieldName);

    return field ? field.value : "";
  }

  return "";
};

const AccessEvent = React.memo(() => {
  const { id } = useParams<{ id: string }>();

  const { t } = useTranslation(["accessEvent", "common"]);

  const { data: accessEventData } = useQuery<{
    accessEvent?: IAccessEvent;
  }>(QUERY_ACCESS_EVENT, {
    skip: !id,
    variables: { id },
  });

  let accessType = get(accessEventData, "accessEvent.type") as string;

  const owner = accessEventData?.accessEvent?.owner;

  const ownerPhoto = get(owner, "photo") as { absolutePath: string };
  const ownerEmail = getOwnerField(owner, "email");
  const ownerFullName = getOwnerField(owner, "fullName");
  const ownerCompany =
    getOwnerField(owner, "company") || get(owner, "employeeCompany.title");

  const anonymized = get(accessEventData, "accessEvent.anonymized") as
    | boolean
    | undefined;
  const visitForCompany = get(accessEventData, "accessEvent.company.title") as
    | string
    | undefined;
  const ownerPhone = getOwnerField(owner, "phone");

  let ownerBadge = get(owner, "badge");
  const ownerFunction = get(owner, "function");

  const isMaintenance = get(owner, "isMaintenance");

  if (isMaintenance) {
    accessType = "MmmrMaintenance";
    ownerBadge = get(owner, "badgeNumber");
  }

  const receiverEmail =
    get(accessEventData, "accessEvent.receiver.email") ||
    get(accessEventData, "accessEvent.customReceiver.email");
  const receiverFullName =
    get(accessEventData, "accessEvent.receiver.fullName") ||
    get(accessEventData, "accessEvent.customReceiver.fullName");

  const checkOut = get(accessEventData, "accessEvent.metadata.checkOut") as
    | CheckOutMarker
    | undefined;
  const method = checkOutMethod(checkOut);

  // Absent on every event that was never sent to the questions, so the card is not drawn at all.
  const postCheckinForm = get(
    accessEventData,
    "accessEvent.metadata.postCheckinForm",
  ) as PostCheckinFormMarker | undefined;

  const formState = postCheckinFormState(postCheckinForm);
  const checkTime = get(accessEventData, "accessEvent.createdAt");

  const location = get(accessEventData, "accessEvent.entity.title");

  const isAdditionalVisitor = accessType === "AdditionalVisitor";

  const arrivedWithOwner = isAdditionalVisitor
    ? (owner as IAdditionalVisitor | undefined)?.arrivedWith ?? undefined
    : undefined;

  const arrivedWithName = arrivedWithOwner
    ? getOwnerField(arrivedWithOwner, "fullName")
    : "";

  let additionalInfo = !!ownerBadge || !!ownerFunction || !!ownerCompany;

  let additionalFields: ICustomField[] = [];

  if (owner && "fields" in owner) {
    additionalFields = owner.fields.filter(
      (field) =>
        !["email", "fullName", "phone", "company"].includes(field.name),
    );

    additionalInfo = additionalInfo || additionalFields.length !== 0;
  }

  // Separate models with separate codes, so they are listed one after the other, never merged.
  // A Guest carries its answers in owner.fields above instead.
  const answeredFields = [
    ...(((owner as any)?.visitorFields as ICustomField[] | undefined) ?? []),
    ...(((owner as any)?.manualFields as ICustomField[] | undefined) ?? []),
  ];

  if (answeredFields.length > 0) {
    additionalFields = [...additionalFields, ...answeredFields];
    additionalInfo = true;
  }

  return (
    <div className="container-fluid">
      <h6 className="mb-3">{t("accessEvent:accessEvent.heading.primary")}</h6>

      <Card className="mb-3 p-3">
        {t("accessEvent:accessEvent.card.logType")}
        {t(`accessEvent:type.${accessType}`)}
      </Card>
      {ownerPhoto?.absolutePath && (
        <Card className="mb-3 p-3 d-flex flex-row align-items-center">
          <div>{t("accessEvent:accessEvent.card.photo")}</div>
          <div>
            <img
              src={ownerPhoto?.absolutePath}
              className="rounded-lg ml-3"
              style={{ width: "100px", height: "100px" }}
              alt=""
            />
          </div>
        </Card>
      )}
      {accessType !== "Package" && accessType !== "Food" && (
        <>
          {(!anonymized || !!ownerFullName) && (
            <Card className="mb-3 p-3">
              {t("accessEvent:accessEvent.card.fullName")}
              {ownerFullName}
            </Card>
          )}

          {ownerEmail && (
            <Card className="mb-3 p-3">
              {t("accessEvent:accessEvent.card.email")}
              {ownerEmail}
            </Card>
          )}
          {ownerPhone && (
            <Card className="mb-3 p-3">
              {t("accessEvent:accessEvent.card.ownerPhone")}
              {ownerPhone}
            </Card>
          )}
          <Card className="mb-3 p-3">
            {t("accessEvent:accessEvent.card.checkStatus")}
            {checkOut
              ? t("accessEvent:accessEvent.card.checkOut")
              : t("accessEvent:accessEvent.card.checkIn")}
            {method && ` (${t(`accessEvent:checkOutMethod.${method}`)})`}
          </Card>
          {!!postCheckinForm && (
            <Card className="mb-3 p-3">
              {t("accessEvent:accessEvent.card.postCheckinForm.title")}
              <span
                className={
                  formState === "completed" ? undefined : "text-danger"
                }
              >
                {t(`accessEvent:accessEvent.card.postCheckinForm.${formState}`)}
              </span>
            </Card>
          )}
        </>
      )}
      <Card className="mb-3 p-3">
        {t("accessEvent:accessEvent.card.checkTime")}
        {dayjs(checkTime).local().format("DD-MM-YYYY / HH:mm")}
        {checkOut?.autoDate && (
          <div className="text-muted small">
            {t("accessEvent:accessEvent.card.autoDate", {
              time: dayjs(checkOut.autoDate)
                .local()
                .format("DD-MM-YYYY / HH:mm"),
            })}
          </div>
        )}
      </Card>

      {location && (
        <Card className="mb-3 p-3">
          {t("accessEvent:accessEvent.card.location")}
          {location}
        </Card>
      )}

      {anonymized && visitForCompany && (
        <Card className="mb-3 p-3">
          {accessType === "Employee"
            ? t("accessEvent:accessEvent.card.employeeOfCompany")
            : t("accessEvent:accessEvent.card.visitForCompany")}
          {visitForCompany}
        </Card>
      )}

      {isAdditionalVisitor && !anonymized && (
        <Card className="mb-3 p-3">
          {t("accessEvent:accessEvent.card.cameWith")}
          {arrivedWithName || t("accessEvent:accessEvent.card.cameWithUnknown")}
        </Card>
      )}

      {(!anonymized || !!receiverFullName || !!receiverEmail) &&
        (accessType === "Package" ||
          accessType === "Food" ||
          ((accessType === "Guest" ||
            accessType === "Visitor" ||
            accessType === "MmmrVisitor" ||
            accessType === "MmmrMaintenance" ||
            accessType === "MmmrAdditionalVisitor" ||
            accessType === "AdditionalVisitor") &&
            accessEventData?.accessEvent?.receiver)) && (
          <Accordion defaultActiveKey="0" className="mb-3">
            <Card>
              <Accordion.Toggle as={Card.Header} eventKey="0" className="p-3">
                {accessType === "Package" &&
                  t("accessEvent:accessEvent.card.packageFor")}
                {accessType === "Food" &&
                  t("accessEvent:accessEvent.card.foodFor")}
                {(accessType === "Guest" ||
                  accessType === "Visitor" ||
                  accessType === "MmmrVisitor" ||
                  accessType === "MmmrMaintenance" ||
                  accessType === "MmmrAdditionalVisitor" ||
                  accessType === "AdditionalVisitor") &&
                  t("accessEvent:accessEvent.card.visitFor")}
              </Accordion.Toggle>
              <Accordion.Collapse eventKey="0">
                <Card.Body className="p-3">
                  {t("accessEvent:accessEvent.card.fullName")}
                  {receiverFullName}
                </Card.Body>
              </Accordion.Collapse>
              <Accordion.Collapse eventKey="0">
                <Card.Body className="p-3">
                  {t("accessEvent:accessEvent.card.email")}
                  {receiverEmail}
                </Card.Body>
              </Accordion.Collapse>
            </Card>
          </Accordion>
        )}
      {accessType === "MeetingAttendee" &&
        accessEventData?.accessEvent?.meeting && (
          <Accordion defaultActiveKey="0" className="mb-3">
            <Card>
              <Accordion.Toggle as={Card.Header} eventKey="0" className="p-3">
                {t("accessEvent:accessEvent.card.meeting")}
              </Accordion.Toggle>
              <Accordion.Collapse eventKey="0">
                <Card.Body className="p-3">
                  {t("accessEvent:accessEvent.card.meetingOrganizerFullName")}
                  {accessEventData?.accessEvent?.meeting?.organizer?.fullName}
                </Card.Body>
              </Accordion.Collapse>
              <Accordion.Collapse eventKey="0">
                <Card.Body className="p-3">
                  {t("accessEvent:accessEvent.card.meetingOrganizerEmail")}
                  {accessEventData?.accessEvent?.meeting?.organizer?.email}
                </Card.Body>
              </Accordion.Collapse>
              <Accordion.Collapse eventKey="0">
                <Card.Body className="p-3">
                  {t("accessEvent:accessEvent.card.meetingTime")}
                  {dayjs(accessEventData?.accessEvent?.meeting?.dtstart)
                    .local()
                    .format("DD-MM-YYYY / HH:mm")}
                </Card.Body>
              </Accordion.Collapse>
            </Card>
          </Accordion>
        )}
      {additionalInfo && (
        <Accordion defaultActiveKey="0" className="mb-3">
          <Card>
            <Accordion.Toggle as={Card.Header} eventKey="0" className="p-3">
              {t("accessEvent:accessEvent.card.additionalInfo")}
            </Accordion.Toggle>
            {!!ownerBadge && (
              <Accordion.Collapse eventKey="0">
                <Card.Body className="p-3">
                  {t("accessEvent:accessEvent.card.badge")}
                  {ownerBadge}
                </Card.Body>
              </Accordion.Collapse>
            )}
            {!!ownerFunction && (
              <Accordion.Collapse eventKey="0">
                <Card.Body className="p-3">
                  {t("accessEvent:accessEvent.card.function")}
                  {ownerFunction}
                </Card.Body>
              </Accordion.Collapse>
            )}
            {!!ownerCompany && (
              <Accordion.Collapse eventKey="0">
                <Card.Body className="p-3">
                  {t("accessEvent:accessEvent.card.company")}
                  {ownerCompany}
                </Card.Body>
              </Accordion.Collapse>
            )}
            {additionalFields.map((field) => {
              let fieldValue = field.value;

              if (field.value === "true") {
                fieldValue = "✓";
              }

              if (field.value === "false") {
                fieldValue = "❌";
              }

              return (
                <Accordion.Collapse eventKey="0" key={field.name}>
                  <Card.Body className="p-3">
                    {field.name} :{" "}
                    <span
                      className={clsx("", {
                        "text-success lead font-weight-bold":
                          field.value === "true",
                      })}
                    >
                      {fieldValue}
                    </span>
                  </Card.Body>
                </Accordion.Collapse>
              );
            })}
          </Card>
        </Accordion>
      )}
    </div>
  );
});

export default AccessEvent;
